import { useState } from 'react';
import { Pipette } from 'lucide-react';

interface ColorTokenInputProps {
  id: string;
  label: string;
  description?: string;
  value: string;
  onChange: (value: string) => void;
  readOnly?: boolean;
}

export function ColorTokenInput({
  id,
  label,
  description,
  value,
  onChange,
  readOnly = false,
}: ColorTokenInputProps) {
  const [localValue, setLocalValue] = useState(value);
  const [isValid, setIsValid] = useState(true);

  // Validate HEX color format
  const validateHex = (hex: string): boolean => {
    return /^#[0-9A-Fa-f]{6}$/.test(hex);
  };

  const handleChange = (newValue: string) => {
    setLocalValue(newValue);

    // Validate and propagate if valid
    if (validateHex(newValue)) {
      setIsValid(true);
      onChange(newValue);
    } else {
      setIsValid(false);
    }
  };

  const handleBlur = () => {
    // On blur, revert to last valid value if invalid
    if (!isValid) {
      setLocalValue(value);
      setIsValid(true);
    }
  };

  return (
    <div className="token-input space-y-2">
      {/* Label */}
      <div>
        <label htmlFor={id} className="block text-sm font-medium text-gray-700">
          {label}
        </label>
        {description && (
          <p className="text-xs text-gray-500 mt-0.5">{description}</p>
        )}
      </div>

      {/* Color Control */}
      <div className="flex items-center space-x-2">
        {/* Color Swatch */}
        <div
          className="w-10 h-10 rounded-md border-2 border-gray-300 shadow-sm flex-shrink-0"
          style={{ backgroundColor: isValid ? localValue : '#cccccc' }}
          title={localValue}
        />

        {/* HEX Input */}
        <div className="flex-1">
          <div className="relative">
            <input
              id={id}
              type="text"
              value={localValue}
              onChange={(e) => handleChange(e.target.value.toUpperCase())}
              onBlur={handleBlur}
              disabled={readOnly}
              className={`input w-full pl-9 font-mono text-sm ${
                !isValid ? 'border-red-500 focus:border-red-500 focus:ring-red-500' : ''
              }`}
              placeholder="#000000"
              pattern="#[0-9A-Fa-f]{6}"
            />
            <Pipette className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-gray-400" />
          </div>
          {!isValid && (
            <p className="text-xs text-red-600 mt-1">
              Formato non valido. Usa #RRGGBB (es. #1E3A5F)
            </p>
          )}
        </div>
      </div>

      {/* Preview Text */}
      <div className="flex items-center space-x-2 text-xs text-gray-500">
        <span>Anteprima:</span>
        <div
          className="px-3 py-1 rounded"
          style={{
            backgroundColor: isValid ? localValue : '#cccccc',
            color: getContrastColor(isValid ? localValue : '#cccccc'),
          }}
        >
          Testo di esempio
        </div>
      </div>
    </div>
  );
}

/**
 * Get contrasting text color (black or white) for background color
 */
function getContrastColor(hexColor: string): string {
  // Convert HEX to RGB
  const r = parseInt(hexColor.slice(1, 3), 16);
  const g = parseInt(hexColor.slice(3, 5), 16);
  const b = parseInt(hexColor.slice(5, 7), 16);

  // Calculate relative luminance
  const luminance = (0.299 * r + 0.587 * g + 0.114 * b) / 255;

  // Return black or white based on luminance
  return luminance > 0.5 ? '#000000' : '#FFFFFF';
}
